import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Expense, isActionable } from '../../models/expense.model';

/** Etiqueta de repercusión a pintar, o null cuando todavía no hay decisión que mostrar. */
interface ChargebackBadge {
  labelKey: string;
  hintKey: string;
  isRebilled: boolean;
}

/**
 * Tarjeta compacta de un gasto, para el desglose de precio del detalle de
 * servicio: miniatura del comprobante, categoría, importe, conductor y
 * descripción, estado y enlace al detalle.
 *
 * Presentacional: no accede a datos ni abre paneles, sólo emite la intención.
 */
@Component({
  selector: 'app-expense-summary-card',
  templateUrl: './expense-summary-card.component.html',
  styleUrls: ['./expense-summary-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpenseSummaryCardComponent {
  @Input({ required: true }) expense!: Expense;

  @Input() currencySymbol: string | null = null;

  @Output() openDetail = new EventEmitter<Expense>();

  /** Los gastos históricos no se pueden abrir: la API no los identifica. */
  protected get canOpenDetail(): boolean {
    return isActionable(this.expense);
  }

  /**
   * El backend resuelve la repercusión al reportar el gasto y la refresca en cada cambio, así
   * que se puede etiquetar desde que está pendiente y quien revisa ve lo que va a costar
   * aprobarlo.
   *
   * `null` es el único caso sin etiqueta: son los gastos históricos y los anteriores a esta
   * funcionalidad, que no llevan la decisión grabada. Todas las sumas los tratan como
   * repercutibles, pero afirmarlo en pantalla sería inventarse un dato que no está.
   */
  protected get chargeback(): ChargebackBadge | null {
    if (this.expense.isRebilledToClient === null) {
      return null;
    }

    return this.expense.isRebilledToClient
      ? {
          labelKey: 'expenses.rebilled',
          hintKey: 'expenses.rebilled_hint',
          isRebilled: true,
        }
      : {
          labelKey: 'expenses.not_rebilled',
          hintKey: 'expenses.not_rebilled_hint',
          isRebilled: false,
        };
  }
}
